/**
 * MetadataEditorService.ts
 *
 * Promise-based facade for the metadata editor modal.
 * Callers `await` the result — no Redux actions needed.
 *
 * Usage in a workflow:
 *   const result = await requestUserValidation(files, context);
 *   if (!result) return; // user cancelled
 *   // proceed with result (PreparedFile[])
 *
 * ─── Session lifecycle & serialization ──────────────────────────────────────
 * The editor is a single modal shared by every workflow (convert / import /
 * upload-update / Google Drive import), so requests are serialized: while one
 * caller keeps the editor busy, the next one waits in a FIFO queue and opens the
 * editor with its own files only afterwards.
 *
 * One session spans one `requestUserValidation` call:
 *   OPEN → user hits Submit (`_resolveWithFiles`, the Promise resolves) → the
 *   caller executes its work while the modal shows "submit in progress" → the
 *   caller calls `_closeEditor()` in its `finally`.
 *
 * The queue slot is therefore released at the END of that lifecycle, not when
 * the Promise resolves: after Submit the caller still owns the modal, and
 * opening the next session earlier would swap the files under a running caller
 * whose `_closeEditor()` would then close somebody else's editor.
 * On cancel the session ends right away — the modal is already closed.
 *
 * Every exit path releases the slot exactly once and settles the Promise:
 * submit + close, cancel, provider unmount, or an exception thrown before the
 * editor opened. So no caller stays pending forever and no failure stalls the
 * queue for the callers behind it.
 *
 * That guarantee holds only while one invariant does: `_closeEditor()` is called
 * by the caller that opened the session and by nobody else — a flow that skipped
 * the editor must not call it. Ownership is never verified, there is no session
 * token: `_closeEditor()` merely ignores a session that is still waiting for the
 * user, and cannot tell the owner from a stray caller in the window between
 * Submit and the owner's own close, where a stray call closes the owner's modal
 * and frees its slot early. Making that impossible requires owner-scoped
 * closing, i.e. a session handle returned by `requestUserValidation`.
 */

import type { PreparableFile, PreparedFile, CreativeBLSettings } from '../contracts';
import { prepareMetadataDraftFiles } from '../metadataDomain';
import { JobMode } from '../legacy_models/model';
import type { Fallback } from '../legacy_models/fallback';

// ─── Singleton dispatch reference ────────────────────────────────────────────
// The MetadataEditorModal component registers its dispatch here on mount.

type DispatchFn = (action: any) => void;

/** One `requestUserValidation` call that currently owns the editor. */
interface EditorSession {
  /** Resolver of the caller's Promise; `null` once it has been settled. */
  resolve: ((files: PreparedFile[] | null) => void) | null;
  /** Frees this session's queue slot. Idempotent. */
  release: () => void;
}

let _dispatch: DispatchFn | null = null;
let _activeSession: EditorSession | null = null;
/** Resolves when the last enqueued session is over. Never rejects. */
let _queueTail: Promise<void> = Promise.resolve();

export function _registerDispatch(dispatch: DispatchFn) {
  _dispatch = dispatch;
}

export function _unregisterDispatch() {
  _dispatch = null;
  // Provider unmounted: settle the open session as cancelled, otherwise its
  // caller (and everyone queued behind it) would wait for a modal that is gone.
  _settleActiveSession(null);
}

export function _setSubmitInProgress(value: boolean) {
  _dispatch?.({ type: 'SET_SUBMIT_IN_PROGRESS', value });
}

/**
 * Settles the active session's Promise if it is still pending.
 *
 * `files === null` (cancel / unmount / editor closed) also ends the session:
 * the modal is gone, so the queue slot is freed for the next caller. After a
 * Submit the session stays active on purpose — the caller is still using the
 * modal and ends the session itself via `_closeEditor()`.
 */
function _settleActiveSession(files: PreparedFile[] | null) {
  const session = _activeSession;

  if (!session) return;

  if (session.resolve) {
    const resolve = session.resolve;
    session.resolve = null;
    resolve(files);
  }

  if (files === null) {
    _activeSession = null;
    session.release();
  }
}

export function _closeEditor() {
  // The owner reaches this point only after its Promise has been resolved with
  // files (Submit), so a session that is still waiting for the user is provably
  // somebody else's: the call came from a flow that never opened an editor.
  // Leave that foreign session alone. This is a safety net, not the contract —
  // callers must not call `_closeEditor()` for a session they did not open (see
  // the ownership note in the file header).
  if (_activeSession?.resolve) return;

  _dispatch?.({ type: 'CLOSE' });
  // Ends the session: the Promise is already settled, this frees the queue slot.
  _settleActiveSession(null);
}

/** Called by the modal's Submit button */
export function _resolveWithFiles(files: PreparedFile[]) {
  // Validate that all files have required fields
  const validatedFiles = files.map((f) => ({
    ...f,
    meta: f.meta || {},
    newFilename: f.newFilename || f.originalName,
  }));

  _settleActiveSession(validatedFiles);
}

/** Called by the modal's Close/Cancel button */
export function _resolveWithCancel() {
  _settleActiveSession(null);
}

// ─── Public Service API ──────────────────────────────────────────────────────

export interface MetadataEditorServiceOptions {
  /** JobMode hint for metadata parsing strategy. Default: UPLOAD */
  mode?: JobMode;
  /** Fallback metadata (cached from previous conversion, per-node, or plain). */
  fallback?: Fallback;
  /** Filenames already occupied by other files in the target folder. */
  occupiedFilenames?: string[];
}

/**
 * Open metadata editor modal and wait for user confirmation.
 *
 * If the editor is already busy with another request, this call waits for its
 * turn (FIFO) and only then opens the editor with its own files.
 *
 * @returns PreparedFile[] if user submitted, null if cancelled (or if the
 *   editor was unmounted while the request was waiting its turn).
 * @throws if the editor is not mounted (MetadataEditorProvider missing).
 */
export async function requestUserValidation(
  files: PreparableFile[],
  context: CreativeBLSettings,
  options: MetadataEditorServiceOptions = {},
): Promise<PreparedFile[] | null> {
  if (!_dispatch) {
    throw new Error(
      'MetadataEditorService: editor not mounted. Wrap your app in <MetadataEditorProvider>.',
    );
  }

  // The queue slot is taken synchronously, before the first await, so the order
  // of the waiting calls matches the order of the calls themselves (FIFO).
  const precedingSessions = _queueTail;
  let releaseSlot!: () => void;
  _queueTail = new Promise<void>((resolve) => {
    releaseSlot = resolve;
  });

  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    releaseSlot();
  };

  try {
    // `_queueTail` never rejects, so waiting here is safe and a caller that blew
    // up with an exception does not stall the callers queued behind it.
    await precedingSessions;

    // The provider may have been unmounted while we were waiting our turn.
    const dispatch = _dispatch;

    if (!dispatch) {
      release();
      return null;
    }

    const mode = options.mode ?? JobMode.UPLOAD;
    const fallback = options.fallback ?? {};
    const editorFiles = prepareMetadataDraftFiles(files, context, mode, fallback);

    const session: EditorSession = { resolve: null, release };
    _activeSession = session;

    // Resolved by the modal's buttons (`_resolveWithFiles` / `_resolveWithCancel`).
    return await new Promise<PreparedFile[] | null>((resolve) => {
      session.resolve = resolve;

      dispatch({
        type: 'OPEN',
        files: editorFiles,
        context,
        occupiedFilenames: options.occupiedFilenames,
      });
    });
  } catch (error) {
    // Thrown before the editor opened (e.g. while preparing the draft files):
    // free the slot, otherwise every following call would wait forever.
    release();
    throw error;
  }
}
